import {
  Box,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import FilterListIcon from "@material-ui/icons/FilterList";
import {
  ContainerPage,
  DialogUpload,
  PermissionGate,
  TitlePage,
  Button
} from "components";
import { usePopup } from "hooks/usePopup";
import { useTableParams } from "hooks/useTableParams";
import { useEffect, useState, useRef } from "react";
import { MdOutlineAutoFixHigh, MdOutlineDelete } from "react-icons/md";
import storageService from "services/storageService";
import { Table } from "shared";
import Cookies from "universal-cookie";
import {
  categoriesSelect,
  dateToday,
  reasonMap,
  subCategoriesSelect
} from "utils";

import ActiveFilters from "./components/ActiveFilters";
import { DialogConfirmDelete } from "./components/DialogConfirmDelete";
import { DialogConfirmType } from "./components/DialogConfirmType";
import { DialogCreateRegister } from "./components/DialogCreateRegister";
import { DialogEdit } from "./components/DialogEdit";
import FilterDrawer from "./components/FilterDrawer";
import { getColumns } from "./tableColumns";

export default function SummaryControl() {
  const cookies = new Cookies();
  const distributionCenterId = cookies.get("distributionCenterId");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // IDs dos motivos que devem vir pré-selecionados
  const defaultReasonIds = [
    23, 22, 27, 3, 19, 18, 25, 24, 12, 11, 10, 7, 6, 28, 16, 8, 4, 5, 31, 21, 1,
    20, 17, 26, 15, 14, 13, 32
  ];

  const { data, onChange, params, refresh, status, count, handleSearch } =
    useTableParams({
      service: params => storageService.getStorageData({ ...params }),
      paramsDefault: {
        multipleFilter: true,
        orderDirection: "-updated",
        excludeReasonId: 9,
        reasonId: defaultReasonIds,
        startDate: dateToday(),
        endDate: dateToday()
      }
    });

  const { addPopup } = usePopup();

  const [reasons, setReasons] = useState([]);
  const [open, setOpen] = useState(false);
  const [dialogEdit, setDialogEdit] = useState({
    isOpen: false,
    data: {}
  });
  const [dialogConfirmDelete, setDialogConfirmDelete] = useState({
    isOpen: false,
    data: {}
  });
  const [dialogConfirmType, setDialogConfirmType] = useState({
    isOpen: false,
    convert: null,
    data: {},
    nameProduct: null
  });
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  // Opções para o menu de ações (Excluir, Corrigir)
  const options = [
    {
      title: "Excluir",
      icon: MdOutlineDelete,
      color: "red",
      permissions: ["Tech", "Estoque", "Estoque físico"],
      action: data => {
        setDialogConfirmDelete({
          isOpen: true,
          data
        });
      }
    },
    {
      title: "Corrigir",
      icon: MdOutlineAutoFixHigh,
      permissions: ["Tech", "Estoque", "Estoque físico"],
      action: data => {
        setDialogEdit({
          isOpen: true,
          data
        });
      }
    }
  ];

  const [columns, setColumns] = useState(() =>
    getColumns(params?.groupBy, options)
  );
  const prevStatusRef = useRef(status);

  const initialData = async () => {
    try {
      const { data } = await storageService.getReason();
      setReasons(
        data
          .filter(({ id }) => id !== 9)
          .map(reason => ({
            id: reason.id,
            label: reasonMap(reason.reason)
          }))
      );
    } catch (error) {
      if (error?.response?.status !== 401) {
        addPopup({
          type: "error",
          title: "Desculpe, ocorreu um erro",
          description: "Erro ao buscar motivos."
        });
      }
    }
  };

  const handleUpdateRegister = async data => {
    await storageService.registerUpdate(data);
  };

  const handleDeleteRegister = async id => {
    await storageService.registerDelete(id);
  };

  const handleApplyFilters = filterParams => {
    onChange(filterParams);
  };

  const handleClearFilters = () => {
    onChange({
      startDate: undefined,
      endDate: undefined,
      reasonId: undefined,
      categoryId: undefined,
      subcategoryId: undefined,
      groupBy: undefined,
      page: 1
    });
  };

  // Prepara os dados de categorias e subcategorias para o Autocomplete
  const categories = categoriesSelect.map(category => ({
    id: category.value,
    label: category.name
  }));

  const subcategories = subCategoriesSelect.map(subcategory => ({
    id: subcategory.value,
    label: subcategory.name
  }));

  useEffect(() => {
    initialData();
  }, []);

  // Atualiza as colunas quando os dados terminam de carregar
  useEffect(() => {
    if (prevStatusRef.current === "pending" && status === "success") {
      setColumns(getColumns(params?.groupBy, options));
    }
    prevStatusRef.current = status;
  }, [status, params?.groupBy]);

  return (
    <>
      <ContainerPage>
        <Box display="flex" justifyContent="space-between" flexWrap="wrap">
          <TitlePage>Controle de Estoque</TitlePage>
        </Box>

        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          ml="auto"
          flexWrap="wrap"
          style={{ gap: "1rem", marginBottom: "1rem" }}
        >
          <PermissionGate permissions={["Tech", "Estoque", "Estoque físico"]}>
            <Button
              variant="contained"
              size="medium"
              startIcon={<AddIcon />}
              onClick={() => {
                setOpen(true);
              }}
            >
              Novo registro
            </Button>
          </PermissionGate>
        </Box>

        <Table
          handleSearch={handleSearch}
          columns={columns}
          data={data}
          onChange={onChange}
          params={params}
          status={status}
          refresh={refresh}
          count={count}
          placeholder="Produto base"
          hover
          filters={
            <>
              <Tooltip title={isMobile ? "" : "Filtrar dados"} arrow>
                <IconButton
                  onClick={() => setIsFilterDrawerOpen(true)}
                  centerRipple={false}
                  disableTouchRipple={false}
                  style={{
                    height: "56px",
                    width: isMobile ? "100%" : "56px",
                    border: "2px solid #e0e0e0",
                    borderRadius: "4px",
                    backgroundColor: "#fff",
                    transition: "all 0.2s ease-in-out",
                    display: "flex",
                    gap: isMobile ? "8px" : "0",
                    fontSize: isMobile ? "1rem" : "inherit",
                    fontWeight: isMobile ? 500 : "inherit"
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor =
                      theme.palette.primary.dark;
                    e.currentTarget.style.backgroundColor = "#f5f5f5";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = "#e0e0e0";
                    e.currentTarget.style.backgroundColor = "#fff";
                  }}
                >
                  <FilterListIcon style={{ fontSize: "24px", color: "#666" }} />
                  {isMobile && <span>Filtrar</span>}
                </IconButton>
              </Tooltip>
              <ActiveFilters
                params={params}
                reasons={reasons}
                categories={categories}
                subcategories={subcategories}
                onChange={onChange}
              />
            </>
          }
        />
      </ContainerPage>

      <DialogUpload
        isOpen={isUploadOpen}
        service={storageService}
        handleClose={setIsUploadOpen}
        refresh={refresh}
        errorMessage="Erro ao atualizar o registro"
        successMessage="Registro atualizado com sucesso"
      />

      <DialogCreateRegister
        isOpen={open}
        handleConfirmType={setDialogConfirmType}
        reasons={reasons}
        setIsOpen={setOpen}
        refresh={refresh}
        distributionCenterId={distributionCenterId}
      />

      <DialogConfirmType
        isOpen={dialogConfirmType.isOpen}
        convert={dialogConfirmType.convert}
        data={dialogConfirmType.data}
        name={dialogConfirmType.nameProduct}
        setIsOpen={setDialogConfirmType}
        refresh={refresh}
        distributionCenterId={distributionCenterId}
      />

      <DialogEdit
        open={dialogEdit.isOpen}
        data={dialogEdit.data}
        handleClose={setDialogEdit}
        handleSubmit={handleUpdateRegister}
        refresh={refresh}
      />

      <DialogConfirmDelete
        open={dialogConfirmDelete.isOpen}
        data={dialogConfirmDelete.data}
        handleClose={setDialogConfirmDelete}
        handleConfirm={handleDeleteRegister}
        refresh={refresh}
      />

      <FilterDrawer
        open={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
        initialParams={params}
        reasons={reasons}
        categories={categories}
        subcategories={subcategories}
      />
    </>
  );
}
